import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, PlusCircle, Users, LogOut, ShieldCheck, ListChecks, UserCog, Menu, X, Settings as SettingsIcon,
} from "lucide-react";
import { useEffect } from "react";
import { Logo } from "@/components/Logo";
import ErrorBoundary from "@/components/ErrorBoundary";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { InstallPrompt } from "@/components/InstallPrompt";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/api";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isStaff = user && (user.role === "admin" || user.role === "agent");
  const isAdmin = user?.role === "admin";

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    toast.success(t("menu.loggedOut"));
    navigate("/");
  };

  const links = isStaff
    ? [
        ...(hasPerm(user, "analytics") ? [{ to: "/app/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-admin-overview" }] : []),
        ...(hasPerm(user, "requests") ? [{ to: "/app/admin/demandes", label: "Demandes", icon: ListChecks, testid: "nav-admin-requests" }] : []),
        ...(hasPerm(user, "clients") ? [{ to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients" }] : []),
        ...(isAdmin ? [{ to: "/app/staff", label: "Équipe", icon: UserCog, testid: "nav-staff" }] : []),
        { to: "/app/reglages", label: "Réglages", icon: SettingsIcon, testid: "nav-settings" },
      ]
    : [
        { to: "/app", label: t("menu.home"), icon: LayoutDashboard, end: true, testid: "nav-home" },
        { to: "/app/nouvelle-demande", label: t("menu.newRequest"), icon: PlusCircle, testid: "nav-new-request" },
        { to: "/app/reglages", label: t("menu.settings"), icon: SettingsIcon, testid: "nav-settings" },
      ];

  const navItem = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-blue-600 text-white shadow-sm shadow-blue-600/30" : "text-slate-600 hover:bg-slate-100"
    }`;

  const NavLinks = ({ testidSuffix = "" }) => (
    <>
      {links.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end} className={navItem} data-testid={`${l.testid}${testidSuffix}`}>
          <l.icon className="h-[18px] w-[18px]" /> {l.label}
        </NavLink>
      ))}
    </>
  );

  const UserBlock = () => (
    <div className="border-t border-slate-100 pt-4">
      <div className="mb-3 flex items-center gap-3 px-2">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
          {user?.name?.charAt(0)?.toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
          <p className="flex items-center gap-1 text-xs text-slate-400">
            {isStaff && <ShieldCheck className="h-3 w-3" />}
            {user?.role === "admin" ? "Super-admin" : user?.role === "agent" ? "Staff" : t("menu.client")}
          </p>
        </div>
      </div>
      <Button variant="ghost" onClick={onLogout} data-testid="logout-btn"
        className="w-full justify-start text-slate-600 hover:text-rose-600">
        <LogOut className="mr-2 h-4 w-4" /> {t("menu.logout")}
      </Button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-slate-200 bg-white px-4 py-5 lg:flex">
        <div className="flex items-center justify-between px-2"><Logo /><div className="flex items-center gap-1">{!isStaff && <LanguageSwitcher />}<NotificationBell align="left" /></div></div>
        <nav className="mt-8 flex-1 space-y-1.5"><NavLinks /></nav>
        <UserBlock />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white px-3 py-3 lg:hidden">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
          className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100">
          <Menu className="h-5 w-5" />
        </button>
        <Logo />
        <div className="flex items-center gap-1">{!isStaff && <LanguageSwitcher />}<NotificationBell align="right" /></div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden" data-testid="mobile-drawer">
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[82vw] flex-col border-r border-slate-200 bg-white px-4 py-5 shadow-2xl animate-fade-up">
            <div className="flex items-center justify-between px-2">
              <Logo />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn" className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-8 flex-1 space-y-1.5"><NavLinks testidSuffix="-mobile" /></nav>
            <UserBlock />
          </aside>
        </div>
      )}

      <main className="flex-1 px-4 pb-16 pt-20 lg:ml-64 lg:px-10 lg:py-10">
        <div className="mx-auto max-w-6xl animate-fade-up">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
      <InstallPrompt />
    </div>
  );
}
